import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faCircleExclamation,
  faCircleInfo,
  faFileExport,
  faTableCells,
} from '@fortawesome/free-solid-svg-icons'
import DaisyDatePicker from '../DaisyDatePicker'
import {
  hrToolsService,
  type PaylocityTimeExport,
} from '../../services/hrToolsService'
import {
  DEFAULT_OPERATION_CONFIG,
  WEEKDAY_LABELS,
  nearbyPayPeriods,
  payPeriodBounds,
  toLocalYmd,
  type PayrollConfig,
  type UpcomingPayPeriod,
} from '../../utils/controlCenterOperationConfig'

export const HR_SELECT_EMPLOYEE_EVENT = 'hr:select-employee'
const OPTIONS_STORAGE_KEY = 'hr.paylocityTimeExport.options'
const CUSTOM_KEY = 'custom'

type StoredExportOptions = {
  includeSalary: boolean
  requireAwayFromOffice: boolean
  omitUnapproved: boolean
  omitUnmatched: boolean
  hoursOnly: boolean
  auditOvertime: boolean
  includeAdvances: boolean
}

const DEFAULT_EXPORT_OPTIONS: StoredExportOptions = {
  includeSalary: false,
  requireAwayFromOffice: false,
  omitUnapproved: false,
  omitUnmatched: false,
  hoursOnly: false,
  auditOvertime: true,
  includeAdvances: true,
}

function readStoredOptions(): StoredExportOptions {
  if (typeof window === 'undefined') return DEFAULT_EXPORT_OPTIONS
  try {
    const raw = window.localStorage.getItem(OPTIONS_STORAGE_KEY)
    if (!raw) return DEFAULT_EXPORT_OPTIONS
    const parsed = JSON.parse(raw) as Partial<StoredExportOptions>
    return {
      includeSalary: parsed.includeSalary === true,
      requireAwayFromOffice: parsed.requireAwayFromOffice === true,
      omitUnapproved: parsed.omitUnapproved === true,
      omitUnmatched: parsed.omitUnmatched === true,
      hoursOnly: parsed.hoursOnly === true,
      auditOvertime: parsed.auditOvertime !== false,
      includeAdvances: parsed.includeAdvances !== false,
    }
  } catch {
    return DEFAULT_EXPORT_OPTIONS
  }
}

function formatHours(hours: number) {
  return String(Math.round((Number(hours) || 0) * 100) / 100)
}

function formatMoney(amount: number) {
  return (Number(amount) || 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  })
}

function formatDeduction(amount: number | undefined) {
  const n = Number(amount) || 0
  return n === 0 ? '—' : formatMoney(n)
}

function formatDay(d: Date) {
  return d.toLocaleDateString()
}

function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

function formatIssueDate(ymd: string) {
  const d = parseYmdLocal(ymd)
  return d ? formatDay(d) : ymd
}

function periodKey(period: UpcomingPayPeriod) {
  return toLocalYmd(period.start)
}

function lastClosedPeriod(periods: UpcomingPayPeriod[], todayYmd: string) {
  const closed = periods.filter((period) => toLocalYmd(period.end) < todayYmd)
  return closed.length ? closed[closed.length - 1] : null
}

function downloadFile(contents: string, fileName: string, mimeType: string) {
  const blob = new Blob([contents], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function openEmployee(employeeId: string) {
  window.dispatchEvent(new CustomEvent(HR_SELECT_EMPLOYEE_EVENT, { detail: { employeeId } }))
}

type ReportIssue = {
  id: string
  severity: 'error' | 'warning'
  text: string
  detail?: React.ReactNode[]
}

function OptionToggle({
  checked,
  onChange,
  disabled,
  title,
  description,
  ariaLabel,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  title: string
  description: string
  ariaLabel: string
}) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors ${checked ? 'border-primary bg-primary/5' : 'border-base-300 bg-base-100 hover:border-base-content/20'} ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
      <input
        type="checkbox"
        className="checkbox checkbox-primary mt-0.5 shrink-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium leading-5">{title}</span>
        <span className="mt-1 block text-xs leading-4 text-base-content/60">{description}</span>
      </span>
    </label>
  )
}

function BusyButton({
  busy,
  disabled,
  onClick,
  icon,
  idleLabel,
  busyLabel,
  variant,
}: {
  busy: boolean
  disabled?: boolean
  onClick: () => void
  icon: typeof faFileExport
  idleLabel: string
  busyLabel: string
  variant: 'primary' | 'outline'
}) {
  return (
    <button
      type="button"
      className={`btn gap-2 ${variant === 'primary' ? 'btn-primary' : 'btn-outline'}`}
      disabled={disabled || busy}
      aria-busy={busy}
      onClick={onClick}
    >
      {busy ? (
        <span className="loading loading-spinner loading-sm" />
      ) : (
        <FontAwesomeIcon icon={icon} />
      )}
      {busy ? busyLabel : idleLabel}
    </button>
  )
}

const HrPaylocityTimeExportCard: React.FC = () => {
  const stored = useMemo(() => readStoredOptions(), [])
  const [config, setConfig] = useState<PayrollConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [auditing, setAuditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [report, setReport] = useState<PaylocityTimeExport | null>(null)
  const [csvDownloaded, setCsvDownloaded] = useState(false)
  const [selectedKey, setSelectedKey] = useState(CUSTOM_KEY)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [includeSalary, setIncludeSalary] = useState(stored.includeSalary)
  const [requireAwayFromOffice, setRequireAwayFromOffice] = useState(stored.requireAwayFromOffice)
  const [omitUnapproved, setOmitUnapproved] = useState(stored.omitUnapproved)
  const [omitUnmatched, setOmitUnmatched] = useState(stored.omitUnmatched)
  const [hoursOnly, setHoursOnly] = useState(stored.hoursOnly)
  const [auditOvertime, setAuditOvertime] = useState(stored.auditOvertime)
  const [includeAdvances, setIncludeAdvances] = useState(stored.includeAdvances)
  const [errorsOpen, setErrorsOpen] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getPayrollConfig()
        if (cancelled) return
        setConfig(next)
        const full = { ...DEFAULT_OPERATION_CONFIG, ...next }
        const periods = nearbyPayPeriods(full, new Date(), 6, 2)
        const todayYmd = toLocalYmd(new Date())
        const chosen = lastClosedPeriod(periods, todayYmd) || payPeriodBounds(full)
        setStartDate(toLocalYmd(chosen.start))
        setEndDate(toLocalYmd(chosen.end))
        setSelectedKey(toLocalYmd(chosen.start))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load payroll config')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    setReport(null)
    setError(null)
    setCsvDownloaded(false)
  }, [startDate, endDate, includeSalary, requireAwayFromOffice, omitUnapproved, omitUnmatched, hoursOnly, auditOvertime, includeAdvances])

  useEffect(() => {
    try {
      window.localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify({
        includeSalary,
        requireAwayFromOffice,
        omitUnapproved,
        omitUnmatched,
        hoursOnly,
        auditOvertime,
        includeAdvances,
      }))
    } catch {
      // Ignore quota / private-mode failures.
    }
  }, [includeSalary, requireAwayFromOffice, omitUnapproved, omitUnmatched, hoursOnly, auditOvertime, includeAdvances])

  const fullConfig = useMemo(
    () => (config ? { ...DEFAULT_OPERATION_CONFIG, ...config } : null),
    [config],
  )

  const periods = useMemo(
    () => (fullConfig ? nearbyPayPeriods(fullConfig, new Date(), 6, 2) : []),
    [fullConfig],
  )

  const currentKey = useMemo(() => {
    if (!fullConfig) return ''
    return toLocalYmd(payPeriodBounds(fullConfig).start)
  }, [fullConfig])

  const start = parseYmdLocal(startDate)
  const end = parseYmdLocal(endDate)
  const rangeInvalid = Boolean(start && end && start.getTime() > end.getTime())
  const todayYmd = toLocalYmd(new Date())
  const periodOpen = Boolean(endDate && endDate >= todayYmd)
  const weekdayName = fullConfig
    ? WEEKDAY_LABELS[fullConfig.payDateWeekday] || 'Friday'
    : 'Friday'
  const customRange = selectedKey === CUSTOM_KEY

  const applyPeriod = (key: string) => {
    setSelectedKey(key)
    if (key === CUSTOM_KEY) return
    const period = periods.find((item) => periodKey(item) === key)
    if (!period) return
    setStartDate(toLocalYmd(period.start))
    setEndDate(toLocalYmd(period.end))
  }

  const syncKeyFromDates = (nextStart: string, nextEnd: string) => {
    const match = periods.find(
      (period) => toLocalYmd(period.start) === nextStart && toLocalYmd(period.end) === nextEnd,
    )
    setSelectedKey(match ? periodKey(match) : CUSTOM_KEY)
  }

  const onStartChange = (value: string) => {
    setStartDate(value)
    syncKeyFromDates(value, endDate)
  }

  const onEndChange = (value: string) => {
    setEndDate(value)
    syncKeyFromDates(startDate, value)
  }

  const busy = exporting || auditing
  const exportOptions = {
    includeSalary,
    requireAwayFromOffice,
    omitUnapproved,
    omitUnmatched,
    hoursOnly,
    auditOvertime,
    includeAdvances,
  }

  const issues = useMemo<ReportIssue[]>(() => {
    const next: ReportIssue[] = []
    if (error) next.push({ id: 'error', severity: 'error', text: error })
    if (rangeInvalid) {
      next.push({
        id: 'range',
        severity: 'error',
        text: 'Start date must be on or before the end date.',
      })
    }
    if (report) {
      if (report.rowCount === 0) {
        next.push({
          id: 'empty',
          severity: 'warning',
          text: 'No Paylocity rows were generated for this range.',
        })
      }
      if (report.unapproved.length) {
        next.push({
          id: 'unapproved',
          severity: 'warning',
          text: omitUnapproved
            ? `${formatHours(report.totals.unapprovedHours)} hour${report.totals.unapprovedHours === 1 ? '' : 's'} ${report.unapproved.length === 1 ? 'was' : 'were'} left out of the CSV because ${report.unapproved.length === 1 ? 'it is' : 'they are'} not approved in JobTread.`
            : `${formatHours(report.totals.unapprovedHours)} hour${report.totals.unapprovedHours === 1 ? '' : 's'} ${report.unapproved.length === 1 ? 'has' : 'have'} not been approved in JobTread.`,
          detail: report.unapproved.map((row) => (
            `${row.name || row.employeeId} · ${formatIssueDate(row.date)} · ${formatHours(row.hours)} hr${row.hours === 1 ? '' : 's'}`
          )),
        })
      }
      if (report.unmatched.length) {
        next.push({
          id: 'unmatched',
          severity: 'warning',
          text: omitUnmatched
            ? `${report.unmatched.length} JobTread user${report.unmatched.length === 1 ? '' : 's'} could not be matched to a portal employee ID by email and ${report.unmatched.length === 1 ? 'was' : 'were'} left out of the CSV. Add that email on the employee contact in Employee management.`
            : `${report.unmatched.length} JobTread user${report.unmatched.length === 1 ? '' : 's'} could not be matched to a portal employee ID by email. ${report.unmatched.length === 1 ? 'That person was' : 'They were'} exported with ${report.unmatched.length === 1 ? 'their' : 'each'} name in the employee ID column.`,
          detail: report.unmatched.map((row) => (
            `${row.name} · ${formatHours(row.totalHours)} hrs`
          )),
        })
      }
      if (!includeSalary && report.omittedSalary.length) {
        next.push({
          id: 'salary',
          severity: 'warning',
          text: `${report.omittedSalary.length} salary employee${report.omittedSalary.length === 1 ? ' was' : 's were'} omitted from this export.`,
          detail: report.omittedSalary.map((row) => (
            <button
              key={row.employeeId}
              type="button"
              className="link link-hover"
              onClick={() => openEmployee(row.employeeId)}
            >
              {row.name || row.employeeId} · {row.employeeId}
            </button>
          )),
        })
      }
      if (report.overtimeAudit?.length) {
        next.push({
          id: 'overtime-audit',
          severity: 'warning',
          text: `${report.overtimeAudit.length} week${report.overtimeAudit.length === 1 ? '' : 's'} of JobTread overtime do not match hours over 40. The CSV uses the 40-hour rule.`,
          detail: report.overtimeAudit.map((row) => {
            const line = `${row.name || row.employeeId} · week of ${formatIssueDate(row.weekStart)} · JobTread ${formatHours(row.jobtreadOvertimeHours)} OT · payroll ${formatHours(row.payrollOvertimeHours)} OT`
            if (!row.employeeId) return line
            return (
              <button
                key={`${row.employeeId}:${row.weekStart}`}
                type="button"
                className="link link-hover text-left"
                onClick={() => openEmployee(row.employeeId)}
              >
                {line}
              </button>
            )
          }),
        })
      }
      if (!hoursOnly && report.perDiem.skipped.length) {
        next.push({
          id: 'perdiem-skip',
          severity: 'warning',
          text: report.perDiem.skipped.some((row) => row.employeeId)
            ? `Per diem was skipped for ${report.perDiem.skipped.length} Field employee${report.perDiem.skipped.length === 1 ? '' : 's'}.`
            : 'Per diem could not be calculated for this export.',
          detail: report.perDiem.skipped.map((row) => (
            row.name || row.employeeId ? `${row.name || row.employeeId} · ${row.reason}` : row.reason
          )),
        })
      }
    }
    return next
  }, [error, rangeInvalid, report, omitUnapproved, omitUnmatched, includeSalary, hoursOnly])

  const handleExport = async () => {
    if (rangeInvalid || !startDate || !endDate || busy) return
    if (report?.csv) {
      downloadFile(report.csv, report.fileName, 'text/csv;charset=utf-8')
      setCsvDownloaded(true)
      return
    }
    setExporting(true)
    setError(null)
    setReport(null)
    setCsvDownloaded(false)
    try {
      const result = await hrToolsService.exportPaylocityTime(startDate, endDate, exportOptions)
      setReport(result)
      setErrorsOpen(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export Paylocity CSV')
      setErrorsOpen(true)
    } finally {
      setExporting(false)
    }
  }

  const handleAudit = async () => {
    if (rangeInvalid || !startDate || !endDate || busy) return
    setAuditing(true)
    setError(null)
    try {
      const result = await hrToolsService.exportPerDiemAudit(startDate, endDate, {
        includeSalary,
        requireAwayFromOffice,
      })
      if (result.html) downloadFile(result.html, result.fileName, 'text/html;charset=utf-8')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate per diem audit')
      setErrorsOpen(true)
    } finally {
      setAuditing(false)
    }
  }

  const issuesHaveError = issues.some((issue) => issue.severity === 'error')
  const csvReadyToDownload = Boolean(report?.csv)
  const exportIdleLabel = csvReadyToDownload ? 'Download CSV' : 'Preview payroll'
  const exportBusyLabel = 'Previewing…'
  const advanceCategories = report?.advanceDeductions?.categories || []
  const advanceByEmployee = useMemo(() => {
    const map = new Map<string, NonNullable<PaylocityTimeExport['advanceDeductions']>['employees'][number]>()
    for (const row of report?.advanceDeductions?.employees || []) map.set(row.employeeId, row)
    return map
  }, [report])
  const previewRows = useMemo(() => {
    if (!report) return []
    const seen = new Set<string>()
    const rows: Array<{
      key: string
      emp: PaylocityTimeExport['employees'][number] | null
      deductions: PaylocityTimeExport['advanceDeductions']['employees'][number] | undefined
    }> = report.employees.map((emp, index) => {
      const id = emp.unmatched ? '' : String(emp.employeeId || '')
      if (id) seen.add(id)
      return {
        key: `time:${id}:${index}`,
        emp,
        deductions: id ? advanceByEmployee.get(id) : undefined,
      }
    })
    for (const row of report.advanceDeductions?.employees || []) {
      if (seen.has(row.employeeId)) continue
      rows.push({ key: `adv:${row.employeeId}`, emp: null, deductions: row })
    }
    return rows
  }, [report, advanceByEmployee])
  const summaryParts = report
    ? [
      `${report.employeeCount} ${report.employeeCount === 1 ? 'person' : 'people'}`,
      `${formatHours(report.totals.regularHours)} REG`,
      `${formatHours(report.totals.overtimeHours)} OT`,
      ...(!hoursOnly && report.perDiem.totalAmount > 0
        ? [`${formatMoney(report.perDiem.totalAmount)} PDIEM`]
        : []),
      ...advanceCategories
        .filter((category) => (report.advanceDeductions.totals[category.key] || 0) !== 0)
        .map((category) => (
          `${category.label} ${formatMoney(report.advanceDeductions.totals[category.key] || 0)}`
        )),
    ]
    : []
  const showUnapprovedCol = Boolean(report?.employees.some((row) => row.unapprovedHours > 0))
  const paydayNote = report?.advanceDeductions?.payDate
    ? `Payday ${formatIssueDate(report.advanceDeductions.payDate)} · ${report.advanceDeductions.source === 'posted' ? 'posted deductions' : report.advanceDeductions.source === 'projected' ? 'projected deductions' : 'no deductions'}`
    : ''

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faFileExport} />
          Payroll Export
          <div className="dropdown dropdown-end ml-auto">
            <button
              type="button"
              tabIndex={0}
              className="btn btn-ghost btn-xs btn-circle"
              aria-label="How this export works"
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </button>
            <div
              tabIndex={0}
              className="dropdown-content z-20 mt-2 w-80 rounded-box border border-base-300 bg-base-100 p-3 text-sm font-normal shadow-lg"
            >
              <p>Pull JobTread time and download a headerless CSV for Paylocity import.</p>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-4 text-base-content/70">
                <li>Regular hours are coded REG. Overtime is hours over 40 in a week, coded OT.</li>
                <li>Field employees may receive a PDIEM deduction row at $40 per qualifying travel day.</li>
                <li>Payday is the following {weekdayName.toLowerCase()} after the period ends.</li>
                <li>Unmatched JobTread users are exported with their name as the employee ID unless you omit them.</li>
              </ul>
            </div>
          </div>
        </h2>
        <p className="text-sm text-base-content/70">
          Pull JobTread time for a pay period and download a CSV formatted for Paylocity import.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <span className="loading loading-spinner loading-sm" />
            Loading pay periods…
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="space-y-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Pay period</span>
                <select
                  className="select select-bordered w-full max-w-xl"
                  value={selectedKey}
                  onChange={(e) => applyPeriod(e.target.value)}
                  aria-label="Pay period"
                  disabled={busy}
                >
                  {periods.map((period) => {
                    const key = periodKey(period)
                    const currentSuffix = key === currentKey ? ' (current)' : ''
                    return (
                      <option key={key} value={key}>
                        {formatDay(period.start)} – {formatDay(period.end)} · pay {formatDay(period.payDate)}
                        {currentSuffix}
                      </option>
                    )
                  })}
                  <option value={CUSTOM_KEY}>Custom range</option>
                </select>
              </div>
              {customRange ? (
                <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Start</span>
                    <DaisyDatePicker
                      className="w-full"
                      value={startDate}
                      onChange={onStartChange}
                      disabled={busy}
                      aria-label="Pay period start"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">End</span>
                    <DaisyDatePicker
                      className="w-full"
                      value={endDate}
                      onChange={onEndChange}
                      disabled={busy}
                      aria-label="Pay period end"
                    />
                  </div>
                </div>
              ) : null}
              {periodOpen && !rangeInvalid ? (
                <p className="text-xs text-warning">
                  This pay period has not ended yet. JobTread hours may still change.
                </p>
              ) : null}
            </div>

            <div>
              <div className="mb-2 text-sm font-medium">Options</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <OptionToggle
                  checked={auditOvertime}
                  onChange={setAuditOvertime}
                  disabled={busy}
                  title="Audit overtime"
                  description="Flag JobTread overtime that does not match hours over 40 in a week. The CSV always uses the 40-hour rule."
                  ariaLabel="Audit overtime against the 40-hour weekly rule"
                />
                <OptionToggle
                  checked={includeSalary}
                  onChange={setIncludeSalary}
                  disabled={busy}
                  title="Include salary employees"
                  description="Add salary employees to time and per diem. Hourly staff are always included."
                  ariaLabel="Include salary employees in time and per diem"
                />
                <OptionToggle
                  checked={requireAwayFromOffice}
                  onChange={setRequireAwayFromOffice}
                  disabled={busy}
                  title="Require distance from office"
                  description="Also exclude jobs within 10 miles of the Riverton shop. Off: Field and 150 miles from home only."
                  ariaLabel="Require jobs to be away from the Riverton office"
                />
                <OptionToggle
                  checked={omitUnapproved}
                  onChange={setOmitUnapproved}
                  disabled={busy}
                  title="Omit unapproved hours"
                  description="Leave unapproved JobTread time out of the CSV. It is still listed in Issues."
                  ariaLabel="Omit unapproved hours from the CSV"
                />
                <OptionToggle
                  checked={omitUnmatched}
                  onChange={setOmitUnmatched}
                  disabled={busy}
                  title="Omit unmatched users"
                  description="Leave JobTread users with no portal employee ID out of the CSV."
                  ariaLabel="Omit unmatched JobTread users from the CSV"
                />
                <OptionToggle
                  checked={hoursOnly}
                  onChange={setHoursOnly}
                  disabled={busy}
                  title="Hours only"
                  description="Export REG and OT only. Skip PDIEM rows."
                  ariaLabel="Export hours only without per diem"
                />
                <OptionToggle
                  checked={includeAdvances}
                  onChange={setIncludeAdvances}
                  disabled={busy}
                  title="Include advance deductions"
                  description="Show EEAP and other category deductions for this payday. Not added to the CSV."
                  ariaLabel="Include advance deductions in the payroll preview"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap gap-2">
                <BusyButton
                  variant="primary"
                  icon={faFileExport}
                  idleLabel={exportIdleLabel}
                  busyLabel={exportBusyLabel}
                  busy={exporting}
                  disabled={rangeInvalid || !startDate || !endDate || busy}
                  onClick={() => void handleExport()}
                />
                <BusyButton
                  variant="outline"
                  icon={faTableCells}
                  idleLabel="Per diem audit"
                  busyLabel="Building audit…"
                  busy={auditing}
                  disabled={rangeInvalid || !startDate || !endDate || busy}
                  onClick={() => void handleAudit()}
                />
              </div>
            </div>

            {report ? (
              <div className="overflow-x-auto rounded-box border border-base-300">
                <table className="table table-zebra table-sm">
                  <caption className="px-4 py-3 text-left caption-top">
                    <div className="font-medium text-sm text-base-content">{summaryParts.join(' · ')}</div>
                    <div className="mt-1 text-xs font-normal text-base-content/60">
                      {csvDownloaded
                        ? `Downloaded ${report.fileName}`
                        : 'Preview ready. Download when you are satisfied.'}
                      {paydayNote ? ` · ${paydayNote}` : ''}
                    </div>
                  </caption>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th className="text-right">REG</th>
                      <th className="text-right">OT</th>
                      <th className="text-right">Total</th>
                      {showUnapprovedCol ? (
                        <th className="text-right">Unapproved</th>
                      ) : null}
                      {advanceCategories.map((category) => (
                        <th key={category.key} className="text-right">{category.label}</th>
                      ))}
                      {!hoursOnly ? <th className="text-right">PDIEM</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => {
                      const emp = row.emp
                      return (
                        <tr key={row.key}>
                          <td className="whitespace-nowrap font-mono text-xs">
                            {emp?.unmatched ? '—' : emp?.employeeId || row.deductions?.employeeId || '—'}
                          </td>
                          <td>
                            {emp?.name || row.deductions?.name}
                            {emp?.unmatched ? (
                              <span className="ml-2 badge badge-warning badge-sm">Unmatched</span>
                            ) : null}
                            {!emp ? (
                              <span className="ml-2 badge badge-ghost badge-sm">No time</span>
                            ) : null}
                          </td>
                          <td className="text-right tabular-nums">{formatHours(emp?.regularHours || 0)}</td>
                          <td className="text-right tabular-nums">{formatHours(emp?.overtimeHours || 0)}</td>
                          <td className="text-right tabular-nums">{formatHours(emp?.totalHours || 0)}</td>
                          {showUnapprovedCol ? (
                            <td className="text-right tabular-nums">
                              {emp && emp.unapprovedHours > 0 ? formatHours(emp.unapprovedHours) : '—'}
                            </td>
                          ) : null}
                          {advanceCategories.map((category) => (
                            <td key={category.key} className="text-right tabular-nums">
                              {formatDeduction(row.deductions?.amounts[category.key])}
                            </td>
                          ))}
                          {!hoursOnly ? (
                            <td className="text-right tabular-nums">
                              {emp && emp.perDiemAmount > 0 ? formatMoney(emp.perDiemAmount) : '—'}
                            </td>
                          ) : null}
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={2}>Total</th>
                      <th className="text-right tabular-nums">{formatHours(report.totals.regularHours)}</th>
                      <th className="text-right tabular-nums">{formatHours(report.totals.overtimeHours)}</th>
                      <th className="text-right tabular-nums">{formatHours(report.totals.totalHours)}</th>
                      {showUnapprovedCol ? (
                        <th className="text-right tabular-nums">{formatHours(report.totals.unapprovedHours)}</th>
                      ) : null}
                      {advanceCategories.map((category) => (
                        <th key={category.key} className="text-right tabular-nums">
                          {formatDeduction(report.advanceDeductions.totals[category.key])}
                        </th>
                      ))}
                      {!hoursOnly ? (
                        <th className="text-right tabular-nums">
                          {report.perDiem.totalAmount > 0 ? formatMoney(report.perDiem.totalAmount) : '—'}
                        </th>
                      ) : null}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : null}

            {issues.length ? (
              <div className={`rounded-box border p-4 text-sm ${issuesHaveError ? 'border-error bg-error/10' : 'border-warning bg-warning/10'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 font-medium">
                    <FontAwesomeIcon icon={faCircleExclamation} />
                    Issues
                    {!errorsOpen ? (
                      <span className="font-normal text-base-content/60">({issues.length})</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="badge badge-outline h-6 min-h-6 cursor-pointer gap-1 px-2.5 text-xs font-medium"
                    onClick={() => setErrorsOpen((open) => !open)}
                    aria-expanded={errorsOpen}
                  >
                    <FontAwesomeIcon icon={errorsOpen ? faChevronUp : faChevronDown} className="text-[10px]" />
                    {errorsOpen ? 'Collapse' : 'Expand'}
                  </button>
                </div>
                {errorsOpen ? (
                  <ul className="mt-2 space-y-3">
                    {issues.map((issue) => (
                      <li key={issue.id}>
                        <div className={issue.severity === 'error' ? 'text-error font-medium' : undefined}>
                          {issue.text}
                        </div>
                        {issue.detail?.length ? (
                          <ul className="mt-1 list-disc pl-5 text-base-content/80">
                            {issue.detail.map((line, index) => (
                              <li key={issue.id + String(index)}>{line}</li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default HrPaylocityTimeExportCard
